import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { Link } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table'
import { costInvoiceStatusVariant } from '@/lib/status-variants'
import * as Query from '../../../api/gql/query'
import { useRouter } from '../../../hooks'
import { formatIsoDate } from '../../schedule/lib/format'
import InvoiceDialog from '../invoices/InvoiceDialog'
import { useInvoiceMutations } from '../hooks/useInvoiceMutations'
import { describeCostControlError } from '../lib/errors'
import { compareMoneyStrings } from '../lib/contracts'
import { invoiceContractOptions } from '../lib/invoices'
import { INVOICE_STATUS_KEY } from '../lib/labels'
import { formatMoney } from '../lib/money'
import type { CostContract, CostInvoice } from '../types'
import ContractRecordsSkeleton from './ContractRecordsSkeleton'

interface Props { contract: CostContract; contracts: CostContract[]; projectId: string; currency: string; locale: string; canCreate: boolean }

/** A subcontract's invoices (P9 deferred them to 3b); a new one needs the contract ACTIVE or CLOSED (spec §1.4). */
const ContractInvoicesCard: React.FC<Props> = ({ contract, contracts, projectId, currency, locale, canCreate }) => {
  const { t } = useTranslation()
  const { history } = useRouter()
  const mutations = useInvoiceMutations(projectId)
  const [creating, setCreating] = useState(false)
  const { data, loading, error } = useQuery<{ costInvoices: CostInvoice[] }>(Query.COST_INVOICES, {
    variables: { projectId, filter: { contractId: contract.id } },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const rows = data?.costInvoices
  const canAdd = canCreate && (contract.status === 'ACTIVE' || contract.status === 'CLOSED')
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base">{t('CostControl_contract_invoices_title')}</CardTitle>
        {canAdd && (
          <Button variant="outline" size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" />
            {t('CostControl_invoice_new')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {!rows && loading && <ContractRecordsSkeleton />}
        {!rows && error && <p className="text-sm text-destructive">{describeCostControlError(error.message, t)}</p>}
        {rows && rows.length === 0 && <p className="text-sm text-muted-foreground">{t('CostControl_contract_invoices_empty')}</p>}
        {rows && rows.length > 0 && (
          <Table>
            <TableBody>
              {rows.map((i) => (
                <TableRow key={i.id}>
                  <TableCell><Link className="text-primary hover:underline" to={`/project/${projectId}/cost-control/invoices/${i.id}`}>{i.number}</Link></TableCell>
                  <TableCell className="whitespace-nowrap text-sm">{formatIsoDate(t, i.issueDate)}</TableCell>
                  <TableCell className="w-40"><Badge variant={costInvoiceStatusVariant(i.status)}>{t(INVOICE_STATUS_KEY[i.status])}</Badge></TableCell>
                  <TableCell className="w-40 text-right tabular-nums">
                    {formatMoney(i.amount, { locale, currency })}
                    {compareMoneyStrings(i.payable, i.amount) !== 0 && (
                      <div className="text-xs text-muted-foreground">{t('CostControl_ledger_col_payable')} {formatMoney(i.payable, { locale, currency })}</div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <InvoiceDialog
        open={creating}
        mode="create"
        contracts={invoiceContractOptions(contracts)}
        initialContractId={contract.id}
        currency={currency}
        onOpenChange={setCreating}
        onSubmit={async (input) => {
          const res = await mutations.create(input)
          if (res.ok) history.push(`/project/${projectId}/cost-control/invoices/${res.data.createCostInvoice.id}`)
          return res.ok
        }}
      />
    </Card>
  )
}
export default ContractInvoicesCard
